import React from 'react';
import { Alert, Box, CardContent, Chip, CircularProgress } from '@mui/material';
import ChecklistIcon from '@mui/icons-material/Checklist';
import { PageCard, PageContainer, PageHeader } from '../../../../common/components/styled-ui-elements';
import { useClaimsQueue } from '../hooks/useClaimsQueue';
import { useQueueCenter } from '../../../hooks/useQueueCenter';
import CenterSelect from '../../../components/CenterSelect';
import ClaimsGrid from '../components/ClaimsGrid';

/**
 * Claims queue page (US4, FR-023/026-029). Authorization (CAM Edit) is enforced by the
 * route-level `RequireCamEdit` guard (`routes/RequireCamEdit.tsx`) — this page is a
 * straightforward authorized view with no auth branching of its own. Centers are picked one at a
 * time (spec decision #12: no "All Centers" option) and scoped two-way with the app-wide center
 * (`useQueueCenter`); distinct loading/error/empty/data states per Constitution §79/§84. The
 * queue is pinned to `CodingComplete`: `Seeded` cases have nothing to audit yet, so they never
 * appear here.
 */
const ClaimsQueuePage: React.FC = () => {
  const { center, handleCenterChange } = useQueueCenter();

  const state = useClaimsQueue(center, 'CodingComplete');

  return (
    <PageContainer>
      <Box display="flex" justifyContent="center">
        <PageCard>
          <PageHeader
            icon={ChecklistIcon}
            title="Claims"
            subtitle="Claim Audit Manager | Queue"
            action={
              state.status === 'data' ? (
                <Chip label={`${state.items.length} claims`} color="primary" variant="outlined" />
              ) : undefined
            }
          />

          <CardContent sx={{ p: 3, backgroundColor: '#ffffff' }}>
            <Box sx={{ mb: 3 }}>
              <CenterSelect value={center} onChange={handleCenterChange} />
            </Box>

            {state.status === 'empty' && !center && (
              <Alert severity="info" data-testid="claims-queue-select-center">
                Select a center to view its claims queue.
              </Alert>
            )}

            {state.status === 'loading' && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress data-testid="claims-queue-loading" />
              </Box>
            )}

            {state.status === 'error' && (
              <Alert severity="error" data-testid="claims-queue-error">
                Unable to load the claims queue. Please try again.
              </Alert>
            )}

            {state.status === 'empty' && center && (
              <Alert severity="info" data-testid="claims-queue-empty">
                No claims found for this center.
              </Alert>
            )}

            {state.status === 'data' && <ClaimsGrid items={state.items} testId="claims-grid" />}
          </CardContent>
        </PageCard>
      </Box>
    </PageContainer>
  );
};

export default ClaimsQueuePage;
